import React from 'react'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { ListHeaderCell } from './ListHeaderCell'
import { widthSx } from './widthSx'
import type { ListTableColumn, ListTableProps } from './types'
import { renderListCell, monoSx } from '@/shared/listCell'
import { HierarchyKindIcon } from '@/shared/HierarchyKindIcon'
import { isNumericListColumn } from '@/shared/formatListCellValue'
import { listCellHairlineSx, listDensityCellSx, listDensityControlSx, listNumericBodySx } from './listTableChrome'
import { useDensity } from '@/shared/density/DensityProvider'

const INDENT_PX = 16
function defaultGetRowId<TRow extends Record<string, unknown>>(row: TRow): string {
  return String(row.id ?? '')
}

function isGroupRow(row: Record<string, unknown>, isGroupField: string): boolean {
  const val = row[isGroupField]
  return val === true || val === 1 || val === '1' || val === 'true'
}

function defaultRenderCell<TRow extends Record<string, unknown>>(ctx: {
  column: ListTableColumn
  row: TRow
  value: unknown
}): React.ReactNode {
  return renderListCell(ctx.column, ctx.row as Record<string, unknown>)
}

export function ListTable<TRow extends Record<string, unknown> = Record<string, unknown>>(
  props: ListTableProps<TRow>,
) {
  const {
    columns,
    rows,
    loading = false,
    emptyText = 'No data',
    sortField = null,
    sortDir = 'asc',
    onSort,
    onResizeColumn,
    onResetColumnWidth,
    onReorderColumn,
    selection,
    tree = null,
    getRowId = defaultGetRowId,
    depthForRow,
    renderCell = defaultRenderCell,
    onRowClick,
    onRowDoubleClick,
    renderRowActions,
    rowHeight: rowHeightProp,
  } = props

  const { density, tokens } = useDensity()
  const rowHeight = rowHeightProp ?? tokens.listRowHeight
  const cellDensitySx = listDensityCellSx(tokens)
  const controlDensitySx = listDensityControlSx(density)
  const isGroupField = tree?.isGroupField ?? 'is_group'
  const noop = () => undefined

  return (
    <Table size="small" data-testid="list-table">
      <TableHead>
        <TableRow>
          {selection ? (
            <TableCell padding="checkbox" sx={{ width: 42, ...listCellHairlineSx }}>
              <Checkbox
                size="small"
                indeterminate={
                  selection.selectedIds.size > 0 && selection.selectedIds.size < rows.length
                }
                checked={rows.length > 0 && selection.selectedIds.size === rows.length}
                onChange={() => selection.onToggleAllVisible?.()}
                inputProps={{ 'aria-label': 'Select all' }}
              />
            </TableCell>
          ) : null}
          {columns.map((col) => (
            <ListHeaderCell
              key={col.name}
              col={col}
              sortActive={sortField === col.name}
              sortDir={sortDir}
              onSort={() => onSort?.(col.name)}
              onResize={onResizeColumn ?? noop}
              onResetWidth={onResetColumnWidth ?? noop}
              onReorder={onReorderColumn ?? noop}
            />
          ))}
          {renderRowActions ? <TableCell sx={{ width: 1 }} /> : null}
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={columns.length + (selection ? 1 : 0) + (renderRowActions ? 1 : 0)}>
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </TableCell>
          </TableRow>
        ) : rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columns.length + (selection ? 1 : 0) + (renderRowActions ? 1 : 0)}>
              <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => {
            const rowId = getRowId(row)
            const depth = depthForRow?.(row) ?? 0
            return (
              <TableRow
                key={rowId}
                hover
                sx={{ height: rowHeight, cursor: onRowClick || onRowDoubleClick ? 'pointer' : 'default' }}
                onClick={() => onRowClick?.(row)}
                onDoubleClick={() => onRowDoubleClick?.(row)}
              >
                {selection ? (
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()} sx={{ ...cellDensitySx, ...listCellHairlineSx }}>
                    <Checkbox
                      size="small"
                      sx={controlDensitySx}
                      checked={selection.selectedIds.has(rowId)}
                      onChange={() => selection.onToggle(rowId)}
                      inputProps={{ 'aria-label': `Select ${rowId}` }}
                    />
                  </TableCell>
                ) : null}
                {columns.map((col) => {
                  const value = row[col.name]
                  const content = renderCell({ column: col, row, value })
                  const showKindIcon = !!tree && col.name === tree.kindIconColumn
                  const kind = showKindIcon
                    ? (isGroupRow(row, isGroupField) ? 'group' : 'element')
                    : null
                  return (
                    <TableCell
                      key={col.name}
                      data-testid={`list-cell-${rowId}-${col.name}`}
                      sx={{
                        ...widthSx(col.width),
                        ...(col.mono ? monoSx : {}),
                        ...(showKindIcon ? { pl: `${8 + depth * INDENT_PX}px` } : {}),
                        ...cellDensitySx,
                        ...listCellHairlineSx,
                        ...listNumericBodySx(isNumericListColumn({ attr_type: col.attr_type })),
                      }}
                    >
                      {showKindIcon && kind ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <HierarchyKindIcon kind={kind} />
                          {content}
                        </Box>
                      ) : (
                        content
                      )}
                    </TableCell>
                  )
                })}
                {renderRowActions ? (
                  <TableCell onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap', ...cellDensitySx, ...listCellHairlineSx }}>
                    {renderRowActions(row)}
                  </TableCell>
                ) : null}
              </TableRow>
            )
          })
        )}
      </TableBody>
    </Table>
  )
}
